import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { Loader2, Sparkles, Mic, Play, Pause, AlertTriangle, CheckCircle2, MessageCircle, DollarSign, Clock, User, TrendingUp, Lightbulb, RefreshCw, Maximize2 } from "lucide-react";
import { Button } from "../ui/button";

/**
 * Panel de transcription IA d'un appel Aircall.
 *
 * Workflow :
 *   - Si pas de transcription → bouton "Transcrire avec IA"
 *   - Sinon : player audio HTML5 + key moments cliquables (seek) + segments avec timestamps
 *   - Bouton "Réanalyser key moments" pour re-faire passer Gemini sans retélécharger l'audio
 *
 * Props :
 *   api          : axios instance
 *   callLogId    : id de l'appel
 *   recordingUrl : URL audio Aircall (pour le player HTML5)
 *   defaultExpanded : bool — replier par défaut
 */
const KEY_MOMENT_META = {
  intent:      { label: "Signal d'achat", icon: TrendingUp, color: "#22c55e" },
  objection:   { label: "Objection",      icon: AlertTriangle, color: "#f43f5e" },
  prix:        { label: "Prix / budget",  icon: DollarSign, color: "#f59e0b" },
  engagement:  { label: "Engagement",     icon: CheckCircle2, color: "#10b981" },
  decideur:    { label: "Décideur",       icon: User, color: "#8b5cf6" },
  delai:       { label: "Délai",          icon: Clock, color: "#3b82f6" },
  concurrent:  { label: "Concurrent",     icon: AlertTriangle, color: "#f97316" },
  info_clef:   { label: "Info clé",       icon: Lightbulb, color: "#06b6d4" },
};

const IMPORTANCE_RING = {
  high: "ring-2 ring-rose-500/40",
  medium: "ring-1 ring-amber-500/30",
  low: "",
};

const formatTime = (s) => {
  if (s == null) return "0:00";
  const mins = Math.floor(s / 60);
  const secs = Math.floor(s % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

const SENTIMENT_BADGE = {
  positif: { color: "#22c55e", label: "Positif", icon: "😊" },
  neutre: { color: "#64748b", label: "Neutre", icon: "😐" },
  negatif: { color: "#f43f5e", label: "Négatif", icon: "😟" },
};

export default function CallTranscriptionPanel({ api, callLogId, recordingUrl, defaultExpanded = false, testId = "call-transcription-panel" }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [transcribing, setTranscribing] = useState(false);
  const [reanalyzing, setReanalyzing] = useState(false);
  const [expanded, setExpanded] = useState(defaultExpanded);
  const audioRef = useRef(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [playing, setPlaying] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/aircall/calls/${callLogId}/transcription`);
      setData(r.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!callLogId) return;
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callLogId]);

  const transcribe = async () => {
    setTranscribing(true);
    try {
      const r = await api.post(`/aircall/calls/${callLogId}/transcribe`);
      setData((prev) => ({ ...prev, has_transcription: true, transcription: r.data }));
      setExpanded(true);
    } catch (e) {
      alert(e.response?.data?.detail || "Erreur de transcription. Vérifiez l'URL audio.");
    } finally {
      setTranscribing(false);
    }
  };

  const reanalyze = async () => {
    setReanalyzing(true);
    try {
      const r = await api.post(`/aircall/calls/${callLogId}/transcribe/regenerate-analysis`);
      setData((prev) => ({
        ...prev,
        transcription: { ...(prev.transcription || {}), analysis: r.data },
      }));
    } finally {
      setReanalyzing(false);
    }
  };

  const seekTo = (seconds) => {
    if (!audioRef.current) return;
    audioRef.current.currentTime = Math.max(0, seconds);
    audioRef.current.play().catch(() => {});
    setPlaying(true);
  };

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (playing) audioRef.current.pause();
    else audioRef.current.play().catch(() => {});
  };

  if (loading) {
    return (
      <div className="bg-[#0b0811] rounded-xl border border-white/5 p-3" data-testid={`${testId}-loading`}>
        <div className="flex items-center gap-2 text-slate-400 text-xs">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Chargement…
        </div>
      </div>
    );
  }

  if (!data?.has_recording) return null;

  const transcription = data.transcription;
  const analysis = transcription?.analysis;

  // Pas encore transcrit
  if (!data.has_transcription) {
    return (
      <div className="bg-[#0b0811] rounded-xl border border-violet-500/20 p-3" data-testid={`${testId}-empty`}>
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-violet-500/15 flex items-center justify-center">
              <Mic className="w-4 h-4 text-violet-400" />
            </div>
            <div>
              <div className="text-xs font-bold text-white">Transcription IA disponible</div>
              <div className="text-[10px] text-slate-500">Whisper-1 + détection moments-clés Gemini</div>
            </div>
          </div>
          <Button onClick={transcribe} disabled={transcribing} size="sm" className="bg-violet-500 text-white hover:bg-violet-600 text-xs font-bold" data-testid="transcribe-btn">
            {transcribing ? <Loader2 className="w-3 h-3 mr-1.5 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1.5" />}
            {transcribing ? "Transcription… (30-60s)" : "Transcrire avec IA"}
          </Button>
        </div>
      </div>
    );
  }

  const sentimentCfg = SENTIMENT_BADGE[analysis?.sentiment] || SENTIMENT_BADGE.neutre;

  return (
    <div className="bg-[#0b0811] rounded-xl border border-violet-500/20 p-3 space-y-3" data-testid={testId}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-violet-500/15 flex items-center justify-center">
            <Mic className="w-4 h-4 text-violet-400" />
          </div>
          <div>
            <div className="text-xs font-bold text-white flex items-center gap-1.5">
              Transcription IA
              {analysis?.outcome && (
                <span className="text-[9px] px-1.5 py-0.5 rounded bg-violet-500/20 text-violet-300 font-bold uppercase">
                  {analysis.outcome.replace(/_/g, " ")}
                </span>
              )}
              <span className="text-[10px] inline-flex items-center gap-1" style={{ color: sentimentCfg.color }}>
                {sentimentCfg.icon} {sentimentCfg.label}
              </span>
            </div>
            <div className="text-[10px] text-slate-500">
              {transcription?.segments?.length || 0} segments · durée {formatTime(transcription?.duration)}
              {analysis?.source === "fallback" ? " · ⚙️ heuristique" : ` · 🤖 ${transcription?.model || "Whisper"} + ${analysis?.model || "Gemini"}`}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Link to={`/admin/aircall/calls/${callLogId}`} className="text-[10px] px-2 py-1 rounded-md bg-violet-500/15 hover:bg-violet-500/25 text-violet-300 inline-flex items-center gap-1" data-testid="open-fullscreen-btn" title="Ouvrir en plein écran">
            <Maximize2 className="w-3 h-3" />
          </Link>
          <button onClick={reanalyze} disabled={reanalyzing} className="text-[10px] px-2 py-1 rounded-md bg-white/5 hover:bg-white/10 text-slate-300 inline-flex items-center gap-1" data-testid="reanalyze-btn" title="Réanalyser les moments clés">
            {reanalyzing ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
          </button>
          <button onClick={() => setExpanded((v) => !v)} className="text-[11px] px-2 py-1 rounded-md bg-white/5 hover:bg-white/10 text-slate-300" data-testid="toggle-transcription-btn">
            {expanded ? "Masquer" : "Afficher"}
          </button>
        </div>
      </div>

      {analysis?.summary && (
        <p className="text-xs text-slate-300 italic leading-relaxed border-l-2 border-violet-500/40 pl-2">
          {analysis.summary}
        </p>
      )}

      {/* Audio player */}
      {recordingUrl && (
        <div className="bg-[#14101c] rounded-lg border border-white/5 p-2.5" data-testid="audio-player">
          <div className="flex items-center gap-2">
            <button onClick={togglePlay} className="w-8 h-8 rounded-full bg-violet-500 hover:bg-violet-600 text-white flex items-center justify-center" data-testid="audio-play-toggle">
              {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
            </button>
            <div className="flex-1 min-w-0">
              <audio
                ref={audioRef}
                src={recordingUrl}
                onTimeUpdate={() => setCurrentTime(audioRef.current?.currentTime || 0)}
                onPlay={() => setPlaying(true)}
                onPause={() => setPlaying(false)}
                onEnded={() => setPlaying(false)}
                controls
                className="w-full h-8"
              />
            </div>
          </div>
        </div>
      )}

      {/* Key moments */}
      {analysis?.key_moments?.length > 0 && (
        <div data-testid="key-moments">
          <div className="text-[10px] uppercase tracking-wider text-violet-400 font-bold mb-2">
            Moments clés ({analysis.key_moments.length})
          </div>
          <div className="space-y-1.5">
            {analysis.key_moments.map((km, i) => {
              const meta = KEY_MOMENT_META[km.type] || KEY_MOMENT_META.info_clef;
              const Icon = meta.icon;
              const ring = IMPORTANCE_RING[km.importance] || "";
              return (
                <button
                  key={i}
                  onClick={() => seekTo(km.timestamp_start)}
                  className={`w-full text-left bg-[#14101c] hover:bg-white/5 rounded-lg p-2.5 transition-all ${ring}`}
                  data-testid={`key-moment-${i}`}
                  title={`Aller à ${formatTime(km.timestamp_start)}`}
                >
                  <div className="flex items-start gap-2">
                    <div className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: meta.color + "22" }}>
                      <Icon className="w-3.5 h-3.5" style={{ color: meta.color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: meta.color }}>{meta.label}</span>
                        <span className="text-[9px] px-1.5 py-0.5 rounded bg-white/5 text-slate-400 font-mono">{formatTime(km.timestamp_start)}</span>
                        {km.importance === "high" && (
                          <span className="text-[9px] px-1.5 py-0.5 rounded bg-rose-500/15 text-rose-300 font-bold uppercase">⚡ critique</span>
                        )}
                      </div>
                      <div className="text-xs text-white mt-0.5 leading-snug">{km.text}</div>
                      {km.why && <div className="text-[10px] text-slate-500 italic mt-0.5">{km.why}</div>}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Next actions */}
      {analysis?.next_actions?.length > 0 && (
        <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-lg p-2.5" data-testid="next-actions">
          <div className="text-[10px] uppercase tracking-wider text-emerald-400 font-bold mb-1.5 flex items-center gap-1">
            <TrendingUp className="w-3 h-3" /> Actions à faire
          </div>
          <ul className="space-y-0.5">
            {analysis.next_actions.map((a, i) => (
              <li key={i} className="text-xs text-emerald-200 flex items-start gap-1.5">
                <span className="text-emerald-400 flex-shrink-0">→</span>
                <span>{a}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Transcript segments */}
      {expanded && transcription?.segments?.length > 0 && (
        <div className="bg-[#14101c] rounded-lg border border-white/5 p-2.5 max-h-72 overflow-y-auto" data-testid="transcription-segments">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-2 sticky top-0 bg-[#14101c] py-1">
            Transcription complète
          </div>
          <div className="space-y-1">
            {transcription.segments.map((seg, i) => {
              const isCurrent = currentTime >= seg.start && currentTime <= seg.end;
              return (
                <button
                  key={i}
                  onClick={() => seekTo(seg.start)}
                  className={`w-full text-left flex items-start gap-2 text-xs leading-relaxed py-0.5 rounded transition-colors ${isCurrent ? "bg-violet-500/15 text-white px-1.5" : "text-slate-400 hover:text-white"}`}
                  data-testid={`segment-${i}`}
                >
                  <span className="text-[9px] text-slate-500 font-mono flex-shrink-0 mt-0.5 w-10">
                    {formatTime(seg.start)}
                  </span>
                  <span className="flex-1">{seg.text}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
